import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { MessageSquare, Video, ExternalLink, Hash } from "lucide-react";

const TEAMS = "#5B5FC7";

export default function TeamsPage() {
  const [channels, setChannels] = useState([]);
  const [meetings, setMeetings] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [c, m] = await Promise.all([api.get("/teams/channels"), api.get("/teams/meetings")]);
        setChannels(c.data);
        setMeetings(m.data);
      } catch {}
    })();
  }, []);

  return (
    <div className="min-h-screen p-8 md:p-12" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
      <div className="mb-8">
        <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
          Integration · Microsoft Teams
        </p>
        <h1 className="mt-2 text-4xl tracking-tight" style={{ fontFamily: '"Work Sans", sans-serif' }}>
          Teams
        </h1>
        <p className="mt-2 max-w-xl text-sm text-neutral-600">
          Browse channels and meetings linked to your organization. (Mock data — connects to real Graph API when credentials are configured.)
        </p>
      </div>

      <Tabs defaultValue="channels" className="w-full">
        <TabsList className="rounded-none border border-neutral-200 bg-white p-0">
          <TabsTrigger value="channels" className="rounded-none data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-channels">
            <Hash size={13} className="mr-2"/>Channels
          </TabsTrigger>
          <TabsTrigger value="meetings" className="rounded-none data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-meetings">
            <Video size={13} className="mr-2"/>Meetings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="channels" className="mt-6">
          <div className="border border-neutral-200 bg-white">
            {channels.length === 0 ? <Empty/> : (
              <ul>
                {channels.map((c) => (
                  <li key={c.id} className="flex items-center gap-4 border-b border-neutral-100 px-6 py-4 last:border-0" data-testid={`channel-${c.id}`}>
                    <div className="flex h-9 w-9 items-center justify-center text-white" style={{background: TEAMS}}>
                      <Hash size={14}/>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{c.name}</p>
                      <p className="truncate text-xs text-neutral-600">{c.team} · {c.members} members</p>
                    </div>
                    {c.unread > 0 && (
                      <span className="bg-[#D83B01] px-2 py-0.5 text-[10px] uppercase tracking-wider text-white" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                        {c.unread} new
                      </span>
                    )}
                    <Button asChild variant="outline" size="sm" className="rounded-none border-neutral-300">
                      <a href={`https://teams.microsoft.com/l/channel/${c.id}`} target="_blank" rel="noopener noreferrer">
                        Open <ExternalLink size={12} className="ml-1"/>
                      </a>
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        <TabsContent value="meetings" className="mt-6">
          <div className="border border-neutral-200 bg-white">
            {meetings.length === 0 ? <Empty/> : (
              <ul>
                {meetings.map((m) => {
                  const start = new Date(m.start);
                  const end = new Date(m.end);
                  return (
                    <li key={m.id} className="flex items-center gap-4 border-b border-neutral-100 px-6 py-4 last:border-0" data-testid={`meeting-${m.id}`}>
                      <div className="flex h-9 w-9 items-center justify-center text-white" style={{background: TEAMS}}>
                        <Video size={14}/>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{m.subject}</p>
                        <p className="truncate text-xs text-neutral-600">
                          {start.toLocaleString()} → {end.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} · {m.organizer}
                        </p>
                      </div>
                      <Button asChild size="sm" className="rounded-none text-white" style={{background: TEAMS}}>
                        <a href={m.join_url} target="_blank" rel="noopener noreferrer">
                          <MessageSquare size={12} className="mr-1"/>
                          Join
                        </a>
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Empty() {
  return <p className="px-6 py-12 text-center text-sm text-neutral-600">Nothing here yet.</p>;
}
